import { useAtom } from "jotai";
import { useErrorHandler } from "react-error-boundary";

import {
  branchedQuestionsCountAtom,
  currentQuestionIndexAtom,
  landerQuizAtom,
  landerQuizQuestionAtom,
  showLoadingSpinnerAtom,
} from "../../lander-quiz.state";
import {
  LanderQuizQuestionTypeEnum,
  YesNoAnswersTypeEnum,
} from "../../lander-quiz.types";
import {
  grantLanderQuizExtraEntries,
  SmsData,
  trackLanderQuizVariable,
} from "../../lander-quiz.utils";
import { QuestionButton } from "../shared/question-button/question-button.component";

export const LanderQuizFooter: React.FC = () => {
  const [landerQuiz] = useAtom(landerQuizAtom);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useAtom(
    currentQuestionIndexAtom
  );
  const [currentQuestion] = useAtom(
    landerQuizQuestionAtom(currentQuestionIndex)
  );
  const [branchedQuestionsCount, setBranchedQuestionsCount] = useAtom(
    branchedQuestionsCountAtom
  );
  const [, setShowLoadingSpinner] = useAtom(showLoadingSpinnerAtom);
  const handleWidgetError = useErrorHandler();

  const getSmsQuestionAnswer = (): SmsData => {
    const smsQuestion = landerQuiz.questions.find(
      (q) => q.type === LanderQuizQuestionTypeEnum.Sms
    );
    const smsAccepted = smsQuestion?.answer !== "no thanks";
    const phoneNumber =
      smsAccepted && smsQuestion?.answer ? smsQuestion.answer : "no thanks";

    return {
      smsAccepted,
      phoneNumber,
    };
  };

  const nextBtnFn = () => {
    if (!isQuestionAnswered()) return;

    if (currentQuestionIndex + 1 < landerQuiz.questions.length) {
      let YesNoBranchQuestionAnswer = false;
      const quizStep: number = (() => {
        if (
          currentQuestion.type ===
          LanderQuizQuestionTypeEnum.YesNoBranchQuestion
        ) {
          YesNoBranchQuestionAnswer =
            currentQuestion.answer === YesNoAnswersTypeEnum.YES;

          if (YesNoBranchQuestionAnswer) {
            return 1;
          } else {
            setBranchedQuestionsCount(branchedQuestionsCount + 1);
            return 2;
          }
        }
        return 1;
      })();
      trackLanderQuizVariable(currentQuestion, YesNoBranchQuestionAnswer);
      setCurrentQuestionIndex(currentQuestionIndex + quizStep);
    }
  };

  const skipBtnFn = () => {
    window.theme.LanderQuizUtils.handleNextSweepFlowStep();
  };

  const isQuestionAnswered = (): boolean => {
    if (currentQuestion.type == LanderQuizQuestionTypeEnum.MultiOptionGrid) {
      return (
        !!currentQuestion.answer && currentQuestion.answer.split(",").length > 1
      );
    }
    return !!currentQuestion.answer;
  };

  const isLastQuestion =
    currentQuestionIndex + 1 >= landerQuiz.questions.length;

  const submitBtnFn = async () => {
    if (!isQuestionAnswered()) return;

    trackLanderQuizVariable(currentQuestion);

    setShowLoadingSpinner(true);
    await grantLanderQuizExtraEntries(
      getSmsQuestionAnswer(),
      handleWidgetError
    );
    window.theme.LanderQuizUtils.saveDemographicQuizCompletionInSession();
    window.theme.LanderQuizUtils.handleNextSweepFlowStep();
  };

  if (currentQuestion.type === LanderQuizQuestionTypeEnum.Sms) {
    return null;
  }

  return (
    <div className="lander__quiz__footer">
      <div className="lander__quiz__footer--next-btn">
        <QuestionButton
          label={isLastQuestion ? "Submit" : "Next"}
          isSelected={true}
          isDisabled={!isQuestionAnswered()}
          clickFn={isLastQuestion ? submitBtnFn : nextBtnFn}
        />
      </div>
      <button className="lander__quiz__footer--skip-btn" onClick={skipBtnFn}>
        {`No thanks, maybe another time.`}
      </button>
    </div>
  );
};

declare abstract class LanderQuizUtils {
  static handleNextSweepFlowStep(): void;
  static saveDemographicQuizCompletionInSession(): void;
}

declare global {
  interface FKL {
    landerQuizAnsweredOrSkipped: boolean;
    widgetsweepLanderUrl: string;
    widgetproductHandle: string;
    hasPageDonations: boolean;
    isNonProfitSweep: boolean;
  }

  interface Theme {
    LanderQuizUtils: typeof LanderQuizUtils;
  }
}
